import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useWatch } from 'react-hook-form';
import Styles from '@/assets/js/Styles';
import {
  FormControl,
  Box,
  Typography,
  Tooltip,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { DMArtifact, DMMediaModel } from '@/declarations/models/DMMediaModel';
import { GenericMedia } from '@/declarations/GenericMedia';
import TextInput from '@/components/forms/TextInput';
import CheckboxInput from '@/components/forms/CheckboxInput';
import SwitchInput from '@/components/forms/SwitchInput';
import { BannerBlock } from '@/declarations/models/blocks/BannerBlock';
import { MediaResourceType } from '@/declarations/models/MediaResourceType';
import { FinderButton, FinderType } from '@/components/Finder/FinderButton';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { AudioBlock as AudioBlockModel } from '@/declarations/models/blocks/AudioBlock';
import ImageWithFocusPointSelector from '@/components/ImageWithFocusPointSelector';
import { BlockComponent } from '../../../lib/declarations/EditorComponentTypes';
import { BlockPath, useCurrentBlock } from '../../CurrentBlockProvider';
import Container from '../../../../components/Container';
import { BlockSpecificPage } from '../../../lib/declarations/BlockSpecificPage';
import { Api } from '../../../../services/Api';
import SingleDMItem from '../DMBlocks/SingleDMItem';

const SelectedRow: FC<{ icon: React.ReactNode; children: React.ReactNode; onRemove?: () => void }> = ({
  icon,
  children,
  onRemove,
}) => {
  const { t: tCommon } = useTranslation('common');
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        minHeight: '50px',
      }}>
      <Box
        sx={{
          borderBottomLeftRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
          borderTopLeftRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
          backgroundColor: Styles.Colors.MEDIUM_DARK_GREY,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100px',
        }}>
        {icon}
      </Box>
      <Box
        sx={{
          display: 'flex',
          p: 0,
          backgroundColor: '#fff',
          flex: 1,
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottomRightRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
          borderTopRightRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
        }}>
        {children}

        {!!onRemove && (
          <Tooltip title={tCommon('remove')}>
            <IconButton onClick={onRemove}>
              <MaterialSymbol name='do_not_disturb_on' />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export const AudioBlock: BlockComponent = () => {
  const { t: tCommon } = useTranslation('common');
  const { t: tComponents } = useTranslation('components');
  const { blockPath } = useCurrentBlock();
  const [dmArtifact, setDmArtifact] = useState<DMArtifact>();

  const [showLocalAudioFields, setShowLocalAudioFields] = useState(false);

  const mediaObject = useWatch<BlockSpecificPage<BannerBlock>, `${BlockPath}.mediaobject`>({
    name: `${blockPath}.mediaobject`,
  });

  const {
    field: { value: audio, onChange: onChangeAudio },
  } = useController<BlockSpecificPage<AudioBlockModel>, `${BlockPath}.audio`>({
    name: `${blockPath}.audio`,
  });

  const {
    field: { value: source, onChange: onChangeSource },
  } = useController<BlockSpecificPage<AudioBlockModel>, `${BlockPath}.source`>({
    name: `${blockPath}.source`,
  });

  const {
    field: { value: provider, onChange: onChangeProvider },
  } = useController<BlockSpecificPage<AudioBlockModel>, `${BlockPath}.provider`>({
    name: `${blockPath}.provider`,
  });

  const {
    field: { value: dmItem, onChange: onChangeDmItem },
  } = useController<BlockSpecificPage<AudioBlockModel>, `${BlockPath}.dmItem`>({
    name: `${blockPath}.dmItem`,
  });

  const {
    field: { value: iframeSrc, onChange: onChangeIframeSrc },
  } = useController<BlockSpecificPage<AudioBlockModel>, `${BlockPath}.iframeSrc`>({
    name: `${blockPath}.iframeSrc`,
  });

  const {
    field: { value: convertedFromWidget, onChange: onChangeConvertedFromWidget },
  } = useController<BlockSpecificPage<AudioBlockModel>, `${BlockPath}.convertedFromWidget`>({
    name: `${blockPath}.convertedFromWidget`,
  });

  function handleChangeDMobject(item: DMMediaModel | null): void {
    if (item) {
      // set the dmItem with all required DMArtifact properties
      onChangeDmItem({
        uniqueId: item['artifact.uniqueId'],
        title: item['artifact.ingress.title'],
        // add any other required fields from DMArtifact if necessary
      });
      onChangeAudio(null);
    }
  }

  const handleFocusPointChanged = (focusPoint: { x: string; y: string }) => {
    onChangeAudio({
      ...audio,
      local: {
        ...audio?.local,
        x: focusPoint.x,
        y: focusPoint.y,
      },
    });
  };

  useEffect(() => {
    if (dmItem) {
      if (dmItem.uniqueId) {
        const ctx = Api.getDMArtifact(dmItem.uniqueId);

        ctx
          .fetchDirect(null)
          .then((res) => !!res && setDmArtifact(res))
          .finally(ctx.abort);
      }
    } else setDmArtifact(undefined);
  }, [dmItem]);
  return (
    <Container gap={2} mt={-2} column top left fullWidth>
      <Container gap={2} column mt={2} top left fullWidth>
        <Container gap={1} fullWidth left>
          <FormControl>
            <h3 className='block-sub-label'> {tComponents('AudioBlock.source')}:</h3>
            <RadioGroup row value={source} onChange={(e) => onChangeSource(e.target.value)}>
              <FormControlLabel value='ekultur' control={<Radio />} label={tComponents('AudioBlock.sourceEkultur')} />
              <FormControlLabel value='external' control={<Radio />} label={tComponents('AudioBlock.sourceExternal')} />
            </RadioGroup>
          </FormControl>
        </Container>

        {source === 'ekultur' && (
          <Container column gap={1} mt={2} fullWidth>
            <Container fullWidth mb={2}>
              <FinderButton
                type={FinderType.M24}
                overrideText={tComponents('AudioBlock.pickM24Sound')}
                overrideIcon={<MaterialSymbol name='volume_up' />}
                multiSelect={false}
                finderProps={{
                  selectableResourceTypes: [MediaResourceType.AUDIO],
                  onSelectionConfirmed: (selected) => {
                    onChangeAudio(selected[0].source);
                    onChangeDmItem(null);
                  },
                  defaultResourceType: MediaResourceType.AUDIO,
                }}
              />

              <FinderButton
                type={FinderType.DM}
                overrideText={tComponents('AudioBlock.pickDMSound')}
                finderProps={{
                  onSelectionConfirmed: (items: Array<GenericMedia<DMMediaModel>>) =>
                    items.forEach((item) => handleChangeDMobject(item.source)),
                  hasAudioDep: true,
                }}
                multiSelect={false}
              />
            </Container>

            {!!audio && (
              <Container gap={2} column fullWidth sx={{ p: 2, background: '#efefef' }}>
                <SelectedRow
                  icon={<MaterialSymbol name='volume_up' sx={{ fontSize: 48, color: 'white' }} />}
                  onRemove={() => {
                    onChangeAudio(null);
                  }}>
                  <Container sx={{ width: '100%' }} top>
                    {audio?.content?.poster && (
                      <>
                        <ImageWithFocusPointSelector
                          src={`${audio.content?.poster?.url || mediaObject?.url || ''}`}
                          title={audio.content?.poster?.title}
                          alt={audio.content?.poster?.content?.alttext || mediaObject?.local?.caption}
                          maxWidth='200px'
                          x={audio.local?.x || '0'}
                          y={audio.local?.y || '0'}
                          onFocusPointChanged={handleFocusPointChanged}
                        />
                      </>
                    )}
                    <Container sx={{ width: '100%' }} p={2} column left>
                      <Typography sx={{ fontWeight: 400 }}>
                        <strong>{tComponents('AudioBlock.title')}:</strong>{' '}
                        {audio.title || audio.content?.title || 'N/A'} <br />
                        <strong>{tComponents('AudioBlock.file')}:</strong> {audio.filename}
                        <br />
                      </Typography>
                      <Typography variant='caption' color='textSecondary'>
                        {audio.content?.caption}
                      </Typography>
                    </Container>
                  </Container>
                </SelectedRow>

                <Container gap={2} column fullWidth mt={2} left>
                  <TextInput<BlockSpecificPage<AudioBlockModel>>
                    path={`${blockPath}.audio.local.title`}
                    label={tComponents('AudioBlock.localTitle')}
                    fullWidth
                  />
                  <TextInput<BlockSpecificPage<AudioBlockModel>>
                    path={`${blockPath}.audio.local.caption`}
                    label={tComponents('AudioBlock.localCaption')}
                    multiline
                    minRows={2}
                    maxRows={4}
                    fullWidth
                  />
                  <SwitchInput<BlockSpecificPage<AudioBlockModel>>
                    path={`${blockPath}.audio.local.disableCredits`}
                    label={tComponents('AudioBlock.disableCredits')}
                    fullWidth={false}
                  />
                </Container>
              </Container>
            )}

            {!!dmArtifact && (
              <Container gap={2} column fullWidth sx={{ p: 2, background: '#efefef' }}>
                <SingleDMItem dmArtifact={dmArtifact} onRemove={() => onChangeDmItem(null)} />

                <Container gap={2} column fullWidth mt={2} left>
                  <TextInput<BlockSpecificPage<AudioBlockModel>>
                    path={`${blockPath}.dmItem.local.title`}
                    label={tComponents('AudioBlock.localTitle')}
                    fullWidth
                  />
                  <TextInput<BlockSpecificPage<AudioBlockModel>>
                    path={`${blockPath}.dmItem.local.caption`}
                    label={tComponents('AudioBlock.localCaption')}
                    multiline
                    minRows={2}
                    maxRows={4}
                    fullWidth
                  />
                </Container>
              </Container>
            )}
          </Container>
        )}

        {source === 'external' && (
          <Container gap={2} column fullWidth mt={2}>
            <FormControl fullWidth>
              <InputLabel>{tComponents('AudioBlock.provider')}</InputLabel>
              <Select sx={{ width: '250px' }} value={provider ?? ''} onChange={(e) => onChangeProvider(e.target.value)}>
                <MenuItem value='Acast.com'>Acast</MenuItem>
                <MenuItem value='Buzzsprout.com'>Buzzsprout</MenuItem>
                <MenuItem value='Soundcloud.com'>Soundcloud</MenuItem>
                <MenuItem value='Spotify.com'>Spotify</MenuItem>
                <MenuItem value='Podbean.com'>Podbean</MenuItem>
                <MenuItem value='Other'>{tComponents('AudioBlock.other')}</MenuItem>
              </Select>
              <FormHelperText>{tComponents('AudioBlock.providerHelpText')}</FormHelperText>
            </FormControl>

            <Container gap={2} column top left fullWidth>
              <Container gap={2} top left fullWidth>
                <TextInput<BlockSpecificPage<AudioBlockModel>>
                  path={`${blockPath}.html`}
                  label={tComponents('WidgetBlock.html')}
                  type='text'
                  multiline
                  minRows={5}
                  maxRows={10}
                />
              </Container>
              <Container gap={2} top left fullWidth>
                <CheckboxInput<BlockSpecificPage<AudioBlockModel>>
                  path={`${blockPath}.isWidget`}
                  label={tComponents('WidgetBlock.isWidget')}
                />
              </Container>

              <Container
                column
                fullWidth
                px={2}
                py={1}
                mt={0}
                gap={0}
                left
                sx={{
                  backgroundColor: Styles.Colors.LIGHT_BLUE,
                  border: `1px solid ${Styles.Colors.MEDIUM_BLUE}`,
                }}>
                <p dangerouslySetInnerHTML={{ __html: tComponents('AudioBlock.securityInfo') }} />
              </Container>
            </Container>

            <Container gap={1} column top left fullWidth>
              <h3 className='block-sub-label'>{tComponents('WidgetBlock.iframeSettings')}</h3>

              <Container gap={2} top left fullWidth>
                <TextInput<BlockSpecificPage<AudioBlockModel>>
                  path={`${blockPath}.iframeSrc`}
                  label={tComponents('WidgetBlock.src')}
                />
                <Box sx={{ width: '30%' }}>
                  <TextInput<BlockSpecificPage<AudioBlockModel>>
                    path={`${blockPath}.iframeHeight`}
                    label={tComponents('WidgetBlock.height')}
                  />
                </Box>
              </Container>
            </Container>

            <Container gap={1} column top left fullWidth>
              <h3 className='block-sub-label'>{tComponents('WidgetBlock.additionalSettings')}</h3>
              <TextInput<BlockSpecificPage<AudioBlockModel>>
                path={`${blockPath}.externalUrl`}
                label={tComponents('WidgetBlock.externalUrl')}
              />
            </Container>
          </Container>
        )}
      </Container>
    </Container>
  );
};

export default AudioBlock;
